import type { Dayjs } from 'dayjs';
import type { AccountList } from 'src/types/user';

import { useState, useEffect, useImperativeHandle } from 'react';

import { modifiedTypes } from 'src/utils/constants';

import { useTranslate } from 'src/locales';
import { useGetLabels } from 'src/pages/contacts/hooks/useGetLabels';
import { useGetLifecycleStages } from 'src/pages/contacts/hooks/useGetLifecycle';

import type { TableHeadCellProps } from '../components/head/table-head-custom';

// ----------------------------------------------------------------------

type UseTableHeadLogicProps = {
  headCells: TableHeadCellProps[];
  dateTimeFiltre: {
    id: string;
    fromDate: Dayjs;
    toDate: Dayjs;
  }[];
  moduleName?: string;
  resetFilterKey?: number;
  handleFilterChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    column: keyof AccountList
  ) => void;
  setDateTimeFilter?: any;
  setPage: any;
  ref?: React.Ref<{ handleRemoveAllFilter: () => void }>;
};

// ----------------------------------------------------------------------

export function useTableHeadLogic({
  headCells,
  dateTimeFiltre,
  moduleName = '',
  resetFilterKey,
  handleFilterChange,
  setDateTimeFilter,
  setPage,
  ref,
}: UseTableHeadLogicProps) {
  const { t } = useTranslate('translation');

  // Filter state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedColumn, setSelectedColumn] = useState<TableHeadCellProps | null>(null);
  const [filterValue, setFilterValue] = useState<any>('');
  const [allFilterables, setAllFilterables] = useState<{ id: string; value: string }[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  // External data hooks
  const {
    _recordList: labelsList,
    isFetching: isLabelsFetching,
    open: openLabelsList,
    listScrollHandler: labelsListScrollHandler,
    searchValueHandler: labelsSearchValueHandler,
    openListHandler: openLabelsListHandler,
    closeListHandler: closeLabelsListHandler,
    queryParamsHandler: labelsQueryParamsHandler,
  } = useGetLabels();

  const {
    _recordList: lifecycleList,
    isFetching: isLifecycleFetching,
    open: openLifecycleList,
    listScrollHandler: lifecycleListScrollHandler,
    searchValueHandler: lifecycleSearchValueHandler,
    openListHandler: openLifecycleListHandler,
    closeListHandler: closeLifecycleListHandler,
    queryParamsHandler: lifecycleQueryParamsHandler,
  } = useGetLifecycleStages();

  // Constants
  const dateFieldColumns = [
    'callDate',
    'callDateTs',
    'updatedAt',
    'createdAt',
    'createdDate',
    'lastModified',
    'createdTime',
    'lastActivity',
    'date',
  ];

  // Initialize filterable fields
  useEffect(() => {
    const filterableFields = headCells
      .filter((cell) => cell.filterable)
      .map((cell) => ({
        id: cell.id,
        value: allFilterables.find((f) => f.id === cell.id)?.value ?? '',
      }));

    setAllFilterables(filterableFields);
  }, [headCells]);

  // Update filter value when popover opens
  useEffect(() => {
    if (!selectedColumn) return;
    const selectedColumnValue =
      allFilterables.find((col) => col.id === selectedColumn.id)?.value ?? '';
    setFilterValue(selectedColumnValue);

    // Set default value for dateTime field
    const selectedDTColumnValue = dateTimeFiltre.find((f) => f.id === selectedColumn.id) || {
      fromDate: null,
      toDate: null,
    };

    setFromDate(selectedDTColumnValue.fromDate);
    setToDate(selectedDTColumnValue.toDate);
  }, [anchorEl]);

  // Reset all filters when resetFilterKey changes
  useEffect(() => {
    setAllFilterables((prev) => prev.map((f) => ({ ...f, value: '' })));
    setFilterValue('');
    setFromDate(null);
    setToDate(null);
    setAnchorEl(null);
    setSelectedColumn(null);
    setDateTimeFilter([]);
    labelsQueryParamsHandler({ type: 'reset' });
    lifecycleQueryParamsHandler({ type: 'reset' });
    labelsQueryParamsHandler({ type: 'reset' });
    lifecycleQueryParamsHandler({ type: 'reset' });
    setPage(0);
  }, [resetFilterKey]);

  // Handlers
  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, column: TableHeadCellProps) => {
    if (selectedColumn?.id !== column.id) {
      setFilterValue('');
    }
    setSelectedColumn(column);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = () => {
    if (selectedColumn) {
      if (selectedColumn && dateFieldColumns.includes(selectedColumn.id)) {
        setPage(0);
        setDateTimeFilter((prev: any[]) => {
          const existingIndex = prev.findIndex((col) => col.id === selectedColumn.id);
          const updatedItem = { id: selectedColumn.id, fromDate, toDate };

          if (existingIndex !== -1) {
            return prev.map((col, index) => (index === existingIndex ? updatedItem : col));
          }

          return [...prev, updatedItem];
        });

        handleClosePopover();
        return;
      }

      handleFilterChange(
        { target: { value: filterValue } } as React.ChangeEvent<HTMLInputElement>,
        selectedColumn?.id as keyof AccountList
      );

      setAllFilterables((prev) =>
        prev.map((item) =>
          item.id === selectedColumn?.id ? { ...item, value: filterValue } : item
        )
      );
      handleClosePopover();
    }
  };

  const handleResetFilter = () => {
    if (selectedColumn) {
      if (selectedColumn && dateFieldColumns.includes(selectedColumn.id)) {
        setPage(0);
        setDateTimeFilter((prev: any[]) => prev.filter((col) => col.id !== selectedColumn.id));
        handleClosePopover();
        return;
      }

      handleFilterChange(
        { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>,
        selectedColumn?.id as keyof AccountList
      );

      setAllFilterables((prev) =>
        prev.map((item) => (item.id === selectedColumn?.id ? { ...item, value: '' } : item))
      );

      handleClosePopover();
    }

    setFilterValue('');
    setFromDate(null);
    setToDate(null);
    labelsQueryParamsHandler({ type: 'reset' });
    lifecycleQueryParamsHandler({ type: 'reset' });
  };

  const isFilterApplied = (columnId: string): boolean => {
    const textFilter = allFilterables.find((f) => f.id === columnId)?.value;
    if (textFilter && String(textFilter).trim() !== '') {
      return true;
    }

    const dateFilter = dateTimeFiltre.find((f) => f.id === columnId);
    if (dateFilter && dateFilter.fromDate && dateFilter.toDate) {
      return true;
    }

    return false;
  };

  // Expose handleRemoveAllFilter via ref
  useImperativeHandle(ref, () => ({
    handleRemoveAllFilter: handleResetFilter,
  }));

  // Return grouped props
  return {
    filterState: {
      anchorEl,
      selectedColumn,
      filterValue,
      setFilterValue,
      allFilterables,
      fromDate,
      toDate,
      setFromDate,
      setToDate,
      dateFieldColumns,
    },

    filterHandlers: {
      handleOpenPopover,
      handleClosePopover,
      handleApplyFilter,
      handleResetFilter,
      isFilterApplied,
    },

    externalData: {
      labelsList,
      isLabelsFetching,
      openLabelsList,
      labelsListScrollHandler,
      labelsSearchValueHandler,
      openLabelsListHandler,
      closeLabelsListHandler,
      lifecycleList,
      isLifecycleFetching,
      openLifecycleList,
      lifecycleListScrollHandler,
      lifecycleSearchValueHandler,
      openLifecycleListHandler,
      closeLifecycleListHandler,
    },

    commonProps: {
      t,
      modifiedTypes,
      moduleName,
    },
  };
}
